import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import IndianNumberInput from "@/components/shared/IndianNumberInput";
import LocationCombobox from "@/components/shared/LocationCombobox";
import { OPPORTUNITY_TYPES, GENRES, REQUIRED_TALENT, CAN_POST_GIG_TYPES } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Time picker: 24-hour hh:mm dropdown that avoids the native picker bug.
function TimeSelect({ value, onChange, testId }) {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const s = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      options.push(s);
    }
  }
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
      <option value="">Select time</option>
      {options.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

export default function CreateOpportunity() {
  const { activeProfile } = useAuth();
  const { id } = useParams(); // edit mode when id present
  const isEdit = !!id;
  const nav = useNavigate();
  const [initialLoaded, setInitialLoaded] = useState(!isEdit);
  const [locked, setLocked] = useState(false);
  const [form, setForm] = useState({
    title: "", opportunity_type: "gig", description: "",
    date: "", start_time: "", end_time: "",
    budget_min: null, budget_max: null,
    genre: "", required_talent: [], performers_needed: 1,
    pa_system_provided: false,
  });
  const [location, setLocation] = useState(null);

  useEffect(() => {
    if (isEdit) {
      api.get(`/opportunities/${id}`).then(({ data }) => {
        setForm({
          title: data.title, opportunity_type: data.opportunity_type,
          description: data.description || "",
          date: data.date || "", start_time: data.start_time || "", end_time: data.end_time || "",
          budget_min: data.budget_min, budget_max: data.budget_max,
          genre: data.genre || "",
          required_talent: data.required_talent || [],
          performers_needed: data.performers_needed || 1,
          pa_system_provided: !!data.pa_system_provided,
        });
        setLocation({ city: data.location_city, state: data.location_state, label: data.location_label });
        if ((data.application_count || 0) > 0) setLocked(true);
        setInitialLoaded(true);
      }).catch(() => { toast.error("Not found"); nav("/applications?role=poster"); });
    } else if (activeProfile) {
      setLocation({ city: activeProfile.location_city, state: activeProfile.location_state, label: activeProfile.location_label });
    }
  }, [isEdit, id, activeProfile, nav]);

  const toggle = (key, v) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!location) { toast.error("Location required"); return; }
    if (!isEdit && !activeProfile) { toast.error("No active profile"); return; }
    if (form.budget_min && form.budget_max && form.budget_max < form.budget_min) {
      toast.error("Budget max must be ≥ min"); return;
    }
    try {
      const payload = {
        ...form,
        location_city: location.city, location_state: location.state,
        performers_needed: Number(form.performers_needed) || 1,
      };
      if (isEdit) {
        const { data } = await api.patch(`/opportunities/${id}`, payload);
        toast.success("Gig updated");
        nav(`/opportunities/${data.opportunity_id}`);
      } else {
        payload.profile_id = activeProfile.profile_id;
        const { data } = await api.post("/opportunities", payload);
        toast.success("Gig posted");
        nav(`/opportunities/${data.opportunity_id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!isEdit && activeProfile && !CAN_POST_GIG_TYPES.includes(activeProfile.profile_type)) {
    return (
      <AppLayout>
        <Card className="p-8 border-slate-200 max-w-xl">
          <h1 className="font-display text-2xl font-bold">Switch to a Venue / Event Organizer or Band profile</h1>
          <p className="mt-2 text-slate-600">Only Venue / Event Organizer and Band profiles can post gigs.</p>
          <Button className="mt-4" onClick={() => nav("/profiles")}>Go to My Profiles</Button>
        </Card>
      </AppLayout>
    );
  }

  if (!initialLoaded) return <AppLayout><div className="py-20 text-center text-slate-500">Loading…</div></AppLayout>;

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-bold tracking-tight">{isEdit ? "Edit Gig" : "Post a Gig"}</h1>
      {locked && (
        <Card className="mt-4 p-4 border-amber-200 bg-amber-50 max-w-3xl text-sm text-amber-900" data-testid="edit-locked">
          This gig can no longer be edited because an application has already been received.
        </Card>
      )}
      <Card className="mt-6 p-8 border-slate-200 max-w-3xl">
        <fieldset disabled={locked} className={locked ? "opacity-60" : ""}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input data-testid="op-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Acoustic act for Friday night" />
            </div>
            <div>
              <Label>Type</Label>
              <select className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                value={form.opportunity_type} onChange={(e) => setForm({ ...form, opportunity_type: e.target.value })}>
                {OPPORTUNITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Genre</Label>
              <select className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })}>
                <option value="">Any</option>
                {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea data-testid="op-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Location</Label>
              <div className="mt-1"><LocationCombobox value={location} onChange={setLocation} testId="op-location" /></div>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" data-testid="op-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Start time</Label>
              <TimeSelect value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="op-start" />
            </div>
            <div>
              <Label>End time</Label>
              <TimeSelect value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId="op-end" />
            </div>
            <div>
              <Label>Budget min</Label>
              <div className="mt-1">
                <IndianNumberInput value={form.budget_min} onChange={(n) => setForm({ ...form, budget_min: n })} testId="op-min" />
              </div>
            </div>
            <div>
              <Label>Budget max</Label>
              <div className="mt-1">
                <IndianNumberInput value={form.budget_max} onChange={(n) => setForm({ ...form, budget_max: n })} testId="op-max" />
              </div>
            </div>
            <div>
              <Label>No. of performers needed</Label>
              <IndianNumberInput prefix="" value={form.performers_needed}
                onChange={(n) => setForm({ ...form, performers_needed: n })} testId="op-performers" />
            </div>

            <div className="md:col-span-2 flex items-center justify-between border border-slate-200 rounded-lg p-4">
              <div>
                <Label className="text-sm font-semibold">PA System Provided</Label>
                <div className="text-xs text-slate-500 mt-1">Turn on if the venue supplies the PA / speakers.</div>
              </div>
              <Switch checked={form.pa_system_provided}
                onCheckedChange={(v) => setForm({ ...form, pa_system_provided: v })}
                data-testid="pa-toggle" />
            </div>

            <div className="md:col-span-2">
              <Label>Required talent</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {REQUIRED_TALENT.map((t) => (
                  <button key={t} type="button" onClick={() => toggle("required_talent", t)}
                    className={`px-3 py-1.5 rounded-full border text-sm ${form.required_talent.includes(t) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </fieldset>

        <div className="mt-8 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          <Button onClick={submit} disabled={locked} data-testid="op-submit" className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
            {isEdit ? "Save changes" : "Publish gig"}
          </Button>
        </div>
      </Card>
    </AppLayout>
  );
}
